import { useEffect, useMemo, useRef, useState } from 'react';
import { useLoaderData, useNavigate, useNavigation } from '@remix-run/react';
import 'videojs-contrib-ads/dist/videojs.ads.css?url';
import videojs from 'video.js';
import 'videojs-contrib-ads';
import 'videojs-ima';
import 'video.js/dist/video-js.css?url';
import 'videojs-ima/dist/videojs.ima.css?url';
import 'videojs-mux';
// @ts-ignore
import videojsContribEme from 'videojs-contrib-eme';
// @ts-ignore
import youbora from 'youboralib';
import 'youbora-adapter-videojs';
import type Player from 'video.js/dist/types/player';
import isEmpty from 'lodash.isempty';
import { twMerge } from 'tailwind-merge';

import continueWatchingPlugin from './plugins/continue-watching.ts';
import playNextPlugin from './plugins/play-next.ts';
import analyticsPlugin from './plugins/analytics.ts';
import topBarPlugin from './plugins/top-bar.ts';
import jwAnalyticsPlugin from './plugins/jw-analytics';
import chapterMarkersPlugin from './plugins/chapter-markers.ts';
import muxAnalyticsPlugin from './plugins/mux-analytics.ts';
import brazeAnalyticsPlugin from './plugins/braze-analytics.ts';
import pianoAnalyticsPlugin from './plugins/piano-analytics.ts';
import brightcoveAnalyticsPlugin from './plugins/brightcove-analytics';
import persistedSettingsPlugin from './plugins/persisted-settings.ts';
import { PlayerPlugin } from './types.ts';
import { trackPlayerError, trackPlayerSessionEnd } from './tracking.ts';
import {
  isLive,
  getBoolValue,
  getMuxVideoAnalyticsParams,
  getPersistedPlayerSettings,
  disposePlayerAndNavigate,
  setupManifestErrorHandling,
  removeUnlabeledCaptions,
} from './utils/index.ts';
import { drmSetup, getDrmTypeForAnalytics } from './drm/drm-setup.ts';
import type { VideojsOptions } from './types.ts';
import type { Feed } from '~/services/layout/index.ts';
import type { PlayerTrackData } from './tracking.ts';
import type { NavigateFunction } from '@remix-run/react';
import type {
  AnalyticsSharedVars,
  MuxAnalyticsProps,
  BrazeAnalyticsConfiguration,
} from '~/services/analytics/events-names.ts';
import { PlayNext } from './play-next.tsx';
import {
  getMidrollOffsetFromXml,
  isPostrollBreakIdInXml,
  videojsAdvertisement,
} from './advertisement/index.ts';
import { AdsBackButton } from './ads-back-button.tsx';
import { useIsGoogleIma } from './advertisement/use-is-google-ima.ts';
import {
  setFullScreenButton,
  fullScreenChangeListener,
} from './utils/fullScreenButton.ts';
import { setupPlayerHotkeys, SKIP_SECONDS } from './utils/hotkeys.ts';
import { useHideUserWayIcon } from '~/hooks/use-hide-userway-icon.ts';
import { useRedirectOnVideoEnd } from './hooks/use-redirect-on-video-end.ts';
import { pianoAnalytics } from '~/services/analytics/piano-analytics';
import { type CreateRequest } from '~/services/pipes/create-request.ts';
import { requestJsonData } from '~/utils/request-json-data.ts';
import { isValidUrl } from '~/utils/validations.ts';
import { resolveVideoMimeType } from './utils/resolve-video-mime-type.ts';
import { updateErrorDisplayLocalizations } from './utils/custom-error-display.ts';

const playerID = 'vjs_video_3';

export type LoaderData = {
  isMock: boolean;
  analyticsSharedVars: AnalyticsSharedVars;
  isContinueWatchingSyncedFeed: boolean;
  continueWatchingUserIdentifier: string;
  continueWatchingRemoteEventsRequest: CreateRequest;
  screenId: string;
  accountId: string;
  policyKey: string;
  viewerId: string;
  styles: any;
  muxAnalytics: MuxAnalyticsProps;
  brazeAnalytics: BrazeAnalyticsConfiguration;
  baseURL: string;
  layoutIdOverride?: string | null;
  language: string;
  localizations?: Record<string, Record<string, string>>;
};

export default function VideojsPlayer({
  playableItem,
  pluginsData,
  appData,
  isNavigatingBack,
  playNextUrl,
  vmapXml,
}: {
  playableItem: any;
  videoId?: string;
  pluginsData: any;
  appData: any;
  isNavigatingBack: boolean;
  playNextUrl: string | null;
  vmapXml: string;
}) {
  const {
    analyticsSharedVars,
    isMock,
    isContinueWatchingSyncedFeed,
    continueWatchingUserIdentifier,
    continueWatchingRemoteEventsRequest,
    styles,
    muxAnalytics,
    brazeAnalytics,
    baseURL,
    layoutIdOverride,
    language,
    localizations,
  } = useLoaderData<LoaderData>();

  const [isRunningPlayNext, setIsRunningPlayNext] = useState<boolean>(false);
  const [duration, setDuration] = useState<number>(0);
  const [currentPosition, setCurrentPosition] = useState<number>(0);
  const [playNextOverlayDuration, setPlayNextOverlayDuration] =
    useState<number>(0);
  const [playNextFeed, setPlayNextFeed] = useState<Feed | null>(null);
  const [showPlayNextOnPostroll, setShowPlayNextOnPostroll] =
    useState<boolean>(false);
  const [showAdsBackButton, setShowAdsBackButton] = useState<boolean>(false);

  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const youboraRef = useRef<any | null>(null);

  const playNextFeedRef = useRef<Feed | null>(null);
  const isRunningPlayNextRef = useRef(false);
  const closePlayNextRef = useRef<boolean>(false);

  const playerVolume = useRef<number | undefined | null>(null);
  const playerMuted = useRef<boolean | undefined | null>(null);

  // Live metadata tracking refs
  const liveMetadataTimeupdateHandlerRef = useRef<(() => void) | null>(null);
  const currentAssetIdRef = useRef<string | null>(null);
  const lastMetadataFetchTimeRef = useRef<number>(0);

  const vhsXhrOnResponseCleanupRef = useRef<(() => void) | null>(null);

  const manifestBlockedRef = useRef(false);

  const handleTextTracks = (tracks: any) => {
    const persistedSettings = getPersistedPlayerSettings();

    for (let i = 0; i < tracks.length; i++) {
      if (tracks[i].kind === 'captions' || tracks[i].kind === 'subtitles') {
        if (!playNextFeedRef.current) {
          tracks[i].mode = 'disabled';
          return;
        }
        if (persistedSettings?.captionsEnabled) {
          const isSelectedTrack =
            tracks[i].language === persistedSettings?.selectedTrack?.language ||
            tracks[i].label === persistedSettings?.selectedTrack?.label;

          tracks[i].mode = isSelectedTrack ? 'showing' : 'disabled';
        } else {
          tracks[i].mode = 'disabled';
        }
      }
    }
  };

  playNextFeedRef.current = playNextFeed;
  isRunningPlayNextRef.current = isRunningPlayNext;

  const navigate: NavigateFunction = useNavigate();
  const navigation = useNavigation();
  const streamType = useMemo(
    () => (isLive(playableItem) ? 'live' : 'vod'),
    [playableItem]
  );
  const isFreeItem =
    playableItem.extensions?.free || playableItem.extensions?.isFree;
  const analyticsCustomProperties = useMemo(
    () =>
      playableItem.extensions?.analyticsCustomProperties &&
      JSON.stringify(playableItem.extensions?.analyticsCustomProperties),
    [playableItem.extensions?.analyticsCustomProperties]
  );

  const trackData: PlayerTrackData = useMemo(
    () => ({
      ...analyticsSharedVars,
      name: playableItem?.title,
      media_id: playableItem?.id,
      media_type: playableItem.type?.value,
      duration: playableItem.extensions?.duration,
      stream_format: playableItem?.content?.type,
      stream_type: streamType,
      free_or_paid: getBoolValue(isFreeItem) ? 'free' : 'paid',
      analyticsCustomProperties,
      ...(playableItem?.extensions?.drm && {
        drm_type: getDrmTypeForAnalytics(playableItem.extensions.drm),
      }),
    }),
    [
      analyticsSharedVars,
      playableItem?.title,
      playableItem?.id,
      playableItem?.content?.type,
      playableItem?.extensions?.duration,
      playableItem?.extensions?.drm,
      playableItem?.type?.value,
      streamType,
      isFreeItem,
      analyticsCustomProperties,
    ]
  );

  const isPostroll: boolean = useMemo(() => {
    return isPostrollBreakIdInXml(vmapXml);
  }, []);

  const midrollTimeOffset: number = useMemo(() => {
    return getMidrollOffsetFromXml(vmapXml);
  }, []);

  const playNext = (event: any) => {
    if (!playNextUrl || closePlayNextRef.current) return;

    const isClickEvent: boolean = event?.type === 'click';

    if (isPostroll && !isClickEvent) return;

    const persistedSettings = getPersistedPlayerSettings();

    playerVolume.current = persistedSettings?.volume;
    playerMuted.current = persistedSettings?.muted;

    // use the link from the pre-loaded data to avoid pulling the play next feed URL twice
    const selfLink =
      (playNextFeed || playNextFeedRef.current)?.entry?.[0]?.link?.href ||
      playNextUrl;

    disposePlayerAndNavigate({
      player: playerRef.current as Player,
      isContinueWatchingSyncedFeed,
      navigationFunction: () => {
        window.location.href = `${baseURL}/player?self-link=${encodeURIComponent(
          selfLink
        )}`;
      },
    });
  };

  const isLiveRef = useRef<boolean>(false);

  const isInlineModeEnabled = getBoolValue(styles?.enable_inline_mode);

  const enableInlineModeByBreakpoint = {
    mobile: isInlineModeEnabled,
    tablet: isInlineModeEnabled,
    desktop: isInlineModeEnabled,
    largeDesktop: isInlineModeEnabled,
  };

  const videoContainerClasses = twMerge(
    enableInlineModeByBreakpoint.mobile
      ? 'mobile:max-tablet:aspect-video mobile:max-tablet:w-full mobile:max-tablet:overflow-hidden'
      : 'mobile:max-tablet:h-screen mobile:max-tablet:overflow-y-hidden mobile:max-tablet:overscroll-none mobile:max-tablet:scrollbar-hide',
    enableInlineModeByBreakpoint.tablet
      ? 'tablet:max-desktop:aspect-video tablet:max-desktop:w-full tablet:max-desktop:overflow-hidden'
      : 'tablet:max-desktop:h-screen tablet:max-desktop:overflow-y-hidden tablet:max-desktop:overscroll-none tablet:max-desktop:scrollbar-hide',
    enableInlineModeByBreakpoint.desktop
      ? 'desktop:max-large-desktop:aspect-video desktop:max-large-desktop:w-full desktop:max-large-desktop:overflow-hidden'
      : 'desktop:max-large-desktop:h-screen desktop:max-large-desktop:overflow-y-hidden desktop:max-large-desktop:overscroll-none desktop:max-large-desktop:scrollbar-hide',
    enableInlineModeByBreakpoint.largeDesktop
      ? 'large-desktop:aspect-video large-desktop:w-full large-desktop:overflow-hidden'
      : 'large-desktop:h-screen large-desktop:overflow-y-hidden large-desktop:overscroll-none large-desktop:scrollbar-hide'
  );

  const topBarBackButtonVisibilityClasses = twMerge(
    enableInlineModeByBreakpoint.mobile
      ? 'mobile:max-tablet:hidden'
      : 'mobile:max-tablet:block',
    enableInlineModeByBreakpoint.tablet
      ? 'tablet:max-desktop:hidden'
      : 'tablet:max-desktop:block',
    enableInlineModeByBreakpoint.desktop
      ? 'desktop:max-large-desktop:hidden'
      : 'desktop:max-large-desktop:block',
    enableInlineModeByBreakpoint.largeDesktop
      ? 'large-desktop:hidden'
      : 'large-desktop:block'
  );

  const topBarPluginVisibilityClasses = twMerge(
    isInlineModeEnabled && getBoolValue(styles?.enable_video_detail_mobile)
      ? 'mobile:max-tablet:hidden'
      : 'mobile:max-tablet:block',
    isInlineModeEnabled && getBoolValue(styles?.enable_video_detail_tablet)
      ? 'tablet:max-desktop:hidden'
      : 'tablet:max-desktop:block',
    isInlineModeEnabled && getBoolValue(styles?.enable_video_detail_desktop)
      ? 'desktop:max-large-desktop:hidden'
      : 'desktop:max-large-desktop:block',
    isInlineModeEnabled &&
      getBoolValue(styles?.enable_video_detail_large_desktop)
      ? 'large-desktop:hidden'
      : 'large-desktop:block'
  );

  const topBarTitlePaddingClass = isInlineModeEnabled ? 'pl-6' : 'pl-20';

  const isPipEnabled =
    typeof styles?.layout_pip_enable === 'undefined'
      ? true
      : getBoolValue(styles?.layout_pip_enable);

  const options: VideojsOptions = useMemo(() => {
    const playerOptions = {
      id: playerID,
      controls: true,
      controlBar: {
        volumePanel: {
          inline: false,
        },
        skipButtons: {
          forward: SKIP_SECONDS,
          backward: SKIP_SECONDS,
        },
        fullscreenToggle: !playNextUrl,
        pictureInPictureToggle: isPipEnabled,
      },
      fill: true,
      autoplay: true,
      bigPlayButton: false,
      experimentalSvgIcons: true,
      liveui: true,
      plugins: {
        continueWatchingPlugin: {
          entryId: playableItem.id,
          resumeTime: playableItem.extensions?.resumeTime,
          entry: playableItem,
          isContinueWatchingSyncedFeed,
          continueWatchingUserIdentifier,
          continueWatchingRemoteEventsRequest,
          continueWatchingPluginConfiguration:
            pluginsData?.continueWatching?.customConfiguration,
          getIsLive: () => isLive(playableItem) || isLiveRef.current,
          baseURL,
        },
        playNextPlugin: {
          setIsRunningPlayNext,
          setPlayNextFeed,
          playNextFeedUrl: playNextUrl,
          setDuration,
          setCurrentPosition,
          playNext,
          isPostroll,
          playNextOverlayDuration,
        },
        analyticsPlugin: { analyticsSharedVars, trackData },
        topBarPlugin: {
          playerID,
          title: playableItem?.title,
          playableItem,
          analyticsTrackData: trackData,
          baseURL,
          isContinueWatchingSyncedFeed,
          layoutIdOverride,
          pluginVisibilityClasses: topBarPluginVisibilityClasses,
          titlePaddingClass: topBarTitlePaddingClass,
          backButtonVisibilityClasses: twMerge(
            topBarPluginVisibilityClasses,
            topBarBackButtonVisibilityClasses
          ),
        },
        chapterMarkersPlugin: {
          playerID,
          playableItem,
          configuration: styles,
          setPlayNextOverlayDuration,
        },
        persistedSettingsPlugin: {
          playerID,
          navigation,
        },
      },
    };

    if (pluginsData?.jw_token) {
      // @ts-ignore
      playerOptions.plugins.jwAnalyticsPlugin = {
        token: pluginsData?.jw_token,
        userId: pluginsData?.jwUserId,
        trackData,
        playableItem,
      };
    }

    if (pluginsData?.pianoAnalytics?.enabled) {
      (playerOptions.plugins as Record<string, unknown>).pianoAnalyticsPlugin =
        {
          trackData,
          playableItem,
          enabled: pluginsData?.pianoAnalytics?.track_player_events,
          eventMapperConfig: pluginsData?.pianoAnalytics?.local_event_mapper,
        };
    }

    if (muxAnalytics?.enabled && muxAnalytics?.env_key) {
      const data = {
        env_key: muxAnalytics?.env_key,
        // Site Metadata
        viewer_user_id: pluginsData?.muxUserId,
        // Player Metadata
        player_name: playerID,
        // Video Metadata
        ...getMuxVideoAnalyticsParams(muxAnalytics, playableItem),
      };

      // @ts-ignore
      playerOptions.plugins.mux = {
        debug: false,
        data,
      };
    }

    if (brazeAnalytics?.enabled) {
      // @ts-ignore
      playerOptions.plugins.brazeAnalyticsPlugin = {
        brazeUserId: pluginsData?.brazeUserId,
        brazeAnalytics,
        trackData,
        playableItem,
      };
    }

    if (pluginsData?.brightcove_account_id) {
      // @ts-ignore
      playerOptions.plugins.brightcoveAnalyticsPlugin = {
        config: {
          accountId: pluginsData.brightcove_account_id,
          playerId: playerID,
          playerName: pluginsData.brightcoveAnalytics?.player_name,
          userId: pluginsData?.brightcoveUserId,
          enableQoE: pluginsData.brightcoveAnalytics?.enable_qoe,
          engagementUpdateInterval:
            pluginsData.brightcoveAnalytics?.engagement_update_interval,
        },
        trackData,
        playableItem,
      };
    }

    return playerOptions;
  }, [
    playableItem,
    playNextUrl,
    analyticsSharedVars,
    trackData,
    navigate,
    isPipEnabled,
  ]);

  useEffect(() => {
    return () => {
      trackPlayerSessionEnd(trackData, 0);
    };
  }, []);

  useEffect(() => {
    document.addEventListener('fullscreenchange', fullScreenChangeListener);
    fullScreenChangeListener();

    return () => {
      document.removeEventListener(
        'fullscreenchange',
        fullScreenChangeListener
      );
      document.documentElement.classList.remove('player-fullscreen-active');
    };
  }, []);

  useEffect(() => {
    if (navigation.state === 'loading') return;
    if (isNavigatingBack) return;

    manifestBlockedRef.current = false;

    const src = playableItem?.content?.src;
    const type = playableItem?.content?.type;
    const resolvedType = resolveVideoMimeType({ declaredType: type, src });

    const { drm } = playableItem.extensions || {};

    const hasDrmConfig = !isEmpty(drm);

    const videoElement = document.createElement('video-js');

    if (!videoElement) return;

    videoElement.classList.add('vjs-big-play-centered');

    const videoContainer = videoRef.current;

    if (!videoContainer) return;
    videoContainer.appendChild(videoElement);

    updateErrorDisplayLocalizations(localizations);

    if (!videojs.getPlugin(PlayerPlugin.continueWatchingPlugin)) {
      videojs.registerPlugin(
        PlayerPlugin.continueWatchingPlugin,
        continueWatchingPlugin
      );
    }

    if (!videojs.getPlugin(PlayerPlugin.playNextPlugin)) {
      videojs.registerPlugin(PlayerPlugin.playNextPlugin, playNextPlugin);
    }

    if (!videojs.getPlugin(PlayerPlugin.analyticsPlugin)) {
      videojs.registerPlugin(PlayerPlugin.analyticsPlugin, analyticsPlugin);
    }

    if (!videojs.getPlugin(PlayerPlugin.topBarPlugin)) {
      videojs.registerPlugin(PlayerPlugin.topBarPlugin, topBarPlugin);
    }

    if (
      !videojs.getPlugin(PlayerPlugin.jwAnalyticsPlugin) &&
      pluginsData?.jw_token
    ) {
      videojs.registerPlugin(PlayerPlugin.jwAnalyticsPlugin, jwAnalyticsPlugin);
    }

    if (!videojs.getPlugin(PlayerPlugin.chapterMarkersPlugin)) {
      videojs.registerPlugin(
        PlayerPlugin.chapterMarkersPlugin,
        chapterMarkersPlugin
      );
    }

    if (
      !videojs.getPlugin(PlayerPlugin.mux) &&
      muxAnalytics?.enabled &&
      muxAnalytics?.env_key
    ) {
      videojs.registerPlugin(PlayerPlugin.mux, muxAnalyticsPlugin);
    }

    if (!videojs.getPlugin(PlayerPlugin.persistedSettingsPlugin)) {
      videojs.registerPlugin(
        PlayerPlugin.persistedSettingsPlugin,
        persistedSettingsPlugin
      );
    }

    if (
      !videojs.getPlugin(PlayerPlugin.brazeAnalyticsPlugin) &&
      brazeAnalytics?.enabled
    ) {
      videojs.registerPlugin(
        PlayerPlugin.brazeAnalyticsPlugin,
        brazeAnalyticsPlugin
      );
    }

    if (
      !videojs.getPlugin(PlayerPlugin.pianoAnalyticsPlugin) &&
      pluginsData?.pianoAnalytics?.enabled
    ) {
      // Initialize Piano Analytics before registering the plugin
      try {
        const { collection_domain, site_id } = pluginsData.pianoAnalytics;

        if (!collection_domain || !site_id) {
          throw new Error(
            `Piano Analytics initialization failed. Missing configuration: collection_domain - ${collection_domain}, site_id - ${site_id}`
          );
        }

        pianoAnalytics.initialize({
          collectionDomain: collection_domain,
          siteId: site_id,
          enabled: pluginsData.pianoAnalytics.enabled,
        });
      } catch (error) {
        console.error(`[Piano Analytics] ${(error as Error).message}`);
      }

      videojs.registerPlugin(
        PlayerPlugin.pianoAnalyticsPlugin,
        pianoAnalyticsPlugin
      );
    }

    if (
      !videojs.getPlugin(PlayerPlugin.brightcoveAnalyticsPlugin) &&
      pluginsData?.brightcove_account_id
    ) {
      videojs.registerPlugin(
        PlayerPlugin.brightcoveAnalyticsPlugin,
        brightcoveAnalyticsPlugin
      );
    }
    playerRef.current = videojs(
      videoElement,
      {
        ...options,
        html5: {
          vhs: {
            // Only force VHS for HLS when there's no DRM. For DASH/MP4, forcing
            // VHS can cause incorrect playlist parsing and noisy retries.
            // Note: resolvedType is normalized by resolveVideoMimeType to ensure
            // all HLS aliases (video/hls, application/vnd.apple.mpegurl) become
            // the canonical application/x-mpegURL.
            // Safari and iOS play HLS natively: forcing VHS there stalls
            // playback until the user seeks.
            overrideNative:
              !hasDrmConfig &&
              resolvedType === 'application/x-mpegURL' &&
              !videojs.browser.IS_ANY_SAFARI &&
              !videojs.browser.IS_IOS,
          },
          nativeAudioTracks: hasDrmConfig, // Required for Safari DRM
          nativeVideoTracks: hasDrmConfig, // Required for Safari DRM
        },
        id: playerID,
      },
      () => {
        videojs.log('player is ready', { playableItem });

        playableItem.extensions?.play_next_feed_url &&
          setFullScreenButton(playerRef.current);
      }
    );

    const player = playerRef.current;
    const removePlayerHotkeys = setupPlayerHotkeys({
      player,
      isPipEnabled,
      useDocumentFullscreen: Boolean(playNextUrl),
    });

    if (typeof playerMuted.current === 'boolean') {
      player.muted(playerMuted.current);
    }
    if (typeof playerVolume.current === 'number') {
      player.volume(playerVolume.current);
    }

    const manifestErrorHandling = setupManifestErrorHandling(
      player,
      manifestBlockedRef,
      localizations,
      language
    );

    const onXhrHooksReady = () => {
      const tech = player.tech({ IWillNotUseThisInPlugins: true } as any);
      const vhs = (tech as any)?.vhs;
      if (vhs?.xhr?.onResponse) {
        vhs.xhr.onResponse(manifestErrorHandling.onVhsXhrResponse);
        vhsXhrOnResponseCleanupRef.current = () => {
          vhs.xhr?.offResponse?.(manifestErrorHandling.onVhsXhrResponse);
        };
      }
    };

    player.one('xhr-hooks-ready', onXhrHooksReady);

    const onNativeHlsError = (): void => {
      const tech: any = player.tech({ IWillNotUseThisInPlugins: true } as any);
      const isNativeHls: boolean =
        resolvedType === 'application/x-mpegURL' && !tech?.vhs;
      if (!isNativeHls || !src || manifestBlockedRef.current) return;
      manifestErrorHandling.detectBlockedManifest(src);
    };

    player.on('error', onNativeHlsError);

    if (typeof youbora !== 'undefined' && pluginsData?.youboraAccountCode) {
      youboraRef.current = new youbora.Plugin({
        accountCode: pluginsData?.youboraAccountCode,
      });
      const youboraAdapter = youboraRef.current;

      const cdnKey = pluginsData?.analyticsMapper?.cdn;

      // Get initial live metadata if available
      const liveMetadata = playableItem?.extensions?.liveMetadata;
      const liveMetadataConfig = playableItem?.extensions?.liveMetadataConfig;

      youboraAdapter.setOptions({
        'app.name': appData?.appName,
        'app.releaseVersion': appData?.appVersion,
        'content.isLive': playableItem?.extensions?.isLive,
        'content.title':
          liveMetadata?.assetId ||
          playableItem?.extensions?.guid ||
          playableItem?.title,
        'content.id': liveMetadata?.programId || playableItem?.id,
        'content.cdn': cdnKey
          ? playableItem?.[cdnKey]
          : playableItem?.extensions?.cdn,
        'content.type': playableItem?.extensions?.programType,
        'user.name': pluginsData?.userId,
      }); // metadata options, https://documentation.npaw.com/integration-docs/docs/setting-options-and-metadata.

      youboraAdapter.setAdapter(new youbora.adapters.Videojs(player)); // Attach adapter

      // Initialize live metadata tracking if available
      if (
        liveMetadata &&
        liveMetadataConfig?.updateUrl &&
        typeof liveMetadataConfig?.updateInterval === 'number' &&
        isFinite(liveMetadataConfig?.updateInterval) &&
        liveMetadataConfig?.updateInterval > 0
      ) {
        currentAssetIdRef.current = liveMetadata.assetId || null;
        lastMetadataFetchTimeRef.current = Date.now();

        const updateInterval = liveMetadataConfig.updateInterval * 1000; // Convert to milliseconds

        const fetchLiveMetadata = async () => {
          try {
            if (!isValidUrl(liveMetadataConfig.updateUrl)) {
              videojs.log('Invalid live metadata update URL');
              return;
            }
            const data = await requestJsonData({
              url: liveMetadataConfig.updateUrl,
            });

            if (!data) {
              videojs.log('Failed to fetch live metadata');
              return;
            }

            const entry = data?.entry?.[0];
            const newLiveMetadata = entry?.extensions?.liveMetadata;

            if (
              newLiveMetadata &&
              newLiveMetadata.assetId !== currentAssetIdRef.current
            ) {
              // Program changed - update Youbora metadata
              currentAssetIdRef.current = newLiveMetadata.assetId;

              youboraAdapter.setOptions({
                'content.title': newLiveMetadata.assetId,
                'content.id': newLiveMetadata.programId,
              });

              videojs.log('Live metadata updated', {
                programId: newLiveMetadata.programId,
                title: newLiveMetadata.assetId,
              });
            }
          } catch (error) {
            videojs.log('Error fetching live metadata', error);
          }
        };

        const timeupdateHandler = () => {
          const now = Date.now();
          if (now - lastMetadataFetchTimeRef.current >= updateInterval) {
            lastMetadataFetchTimeRef.current = now;
            fetchLiveMetadata();
          }
        };

        player.on('timeupdate', timeupdateHandler);
        liveMetadataTimeupdateHandlerRef.current = timeupdateHandler;
      }
    }

    const textTracks: any = player.textTracks();
    const onTextTrackAdded = (): void => {
      removeUnlabeledCaptions(textTracks);
    };
    textTracks.addEventListener('addtrack', onTextTrackAdded);

    player.on('loadedmetadata', () => {
      handleTextTracks(textTracks);

      // @ts-ignore
      const liveTracker = player.liveTracker;
      const isLive = liveTracker && liveTracker.isLive();

      isLiveRef.current = !!isLive;

      if (isLive) {
        // Seek to the live point
        player.currentTime(liveTracker.seekableEnd());
      }
    });

    // Hide context menu for player
    player?.on('contextmenu', function (e: any) {
      e?.preventDefault();
    });

    try {
      if (isMock) {
        player.src({
          src: require('../../mocks/data/movie.mp4'),
          type: 'video/mp4',
        });

        return;
      }
      if (drm) {
        if (!videojs.getPlugin('eme')) {
          videojs.registerPlugin('eme', videojsContribEme);
        }

        const sourceExtras = drmSetup(player, drm, resolvedType);

        player.src([{ src, ...sourceExtras }]);
      } else {
        player.src([{ src, type: resolvedType }]);
      }
    } catch (error) {
      videojs.log('player failed to set video source', { playableItem });

      const err = error as { code?: unknown; message?: string };

      trackPlayerError(trackData, currentPosition || 0, {
        error: err?.code != null ? String(err.code) : 'setup_failure',
        message: err?.message,
      });
    }

    return () => {
      textTracks.removeEventListener('addtrack', onTextTrackAdded);
      player.off('xhr-hooks-ready', onXhrHooksReady);
      player.off('error', onNativeHlsError);
      vhsXhrOnResponseCleanupRef.current?.();
      vhsXhrOnResponseCleanupRef.current = null;
      manifestErrorHandling.teardown();

      setIsRunningPlayNext(false);
      playNextFeedRef.current = null;
      isRunningPlayNextRef.current = false;
      isLiveRef.current = false;

      // Clean up live metadata tracking
      if (liveMetadataTimeupdateHandlerRef.current && player) {
        player.off('timeupdate', liveMetadataTimeupdateHandlerRef.current);
        liveMetadataTimeupdateHandlerRef.current = null;
      }
      currentAssetIdRef.current = null;
      lastMetadataFetchTimeRef.current = 0;

      if (!player) return;

      removePlayerHotkeys();

      if (!document.pictureInPictureElement) player.dispose();
    };
  }, [options, playableItem]);

  const isGoogleImaSdkReady: boolean = useIsGoogleIma();

  useEffect(() => {
    if (!isGoogleImaSdkReady) return;

    videojsAdvertisement({
      playerID,
      player: playerRef.current as Player,
      vmapXml,
      midrollTimeOffset,
      isPostroll,
      setShowAdsBackButton,
      setShowPlayNextOnPostroll,
    });
  }, [options, playableItem, isGoogleImaSdkReady]);

  useHideUserWayIcon();

  useRedirectOnVideoEnd({
    player: playerRef.current,
    playableItem,
    baseURL,
    isPlayNext: !!playNextUrl,
    isContinueWatchingSyncedFeed,
    isInlineModeEnabled,
  });

  return (
    <div data-vjs-player className="player player-container-classes">
      {showAdsBackButton && <AdsBackButton playableItem={playableItem} />}
      <div
        ref={videoRef}
        className={twMerge('player-video-container', videoContainerClasses)}
      />
      {isRunningPlayNext && playNextFeed && !closePlayNextRef.current && (
        <PlayNext
          playNext={playNext}
          feed={playNextFeed}
          totalDuration={duration}
          position={currentPosition}
          isPostroll={isPostroll}
          showPlayNextOnPostroll={showPlayNextOnPostroll}
          closePlayNextRef={closePlayNextRef}
        />
      )}
    </div>
  );
}
